import React, { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useCurrency, useAuth } from '@/App';
import { Switch } from '@/components/ui/switch';
import { ShoppingCart, Trash2, Plus, Minus, UserPlus, Search, UserCheck } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function SalesPage() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');

  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Cliente seleccionado para la venta
  const [isNewCustomerOpen, setIsNewCustomerOpen] = useState(false); // Control del Modal de Nuevo Cliente
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    last_name: '',
    doc_number: '',
    contact_number: '',
    email: ''
  });

  const [docType, setDocType] = useState('ci');

  const { formatPrice, formatPYG, currency, exchangeRate } = useCurrency();
  const { user } = useAuth();

  // Installment State
  const [installmentRules, setInstallmentRules] = useState([]);
  const [isCreditSale, setIsCreditSale] = useState(false);
  const [selectedInstallments, setSelectedInstallments] = useState('');
  const [installmentInterest, setInstallmentInterest] = useState(0);

  // Discount State
  const [discountRates, setDiscountRates] = useState([]);

  const searchInputRef = useRef(null);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    return () => {
      searchInputRef.current = null;
    };
  }, []);


  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/`);
      setProducts(response.data.filter(p => p.stock > 0));
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  useEffect(() => {
    const fetchInstallmentRules = async () => {
      try {
        const response = await axios.get(`${API}/installments`);
        setInstallmentRules(response.data);
      } catch (error) {
        console.error('Error loading installment rules:', error);
      }
    };
    fetchInstallmentRules();

    const fetchDiscountRates = async () => {
      try {
        const response = await axios.get(`${API}/discount-rates/`);
        setDiscountRates(response.data);
      } catch (error) {
        console.error('Error loading discount rates:', error);
      }
    };
    fetchDiscountRates();
  }, []);

  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setCustomerResults([]);
      return;
    }
    try {
      // Asumo que tienes un endpoint para buscar clientes por nombre, email o teléfono
      const response = await axios.get(`${API}/clients/`, { params: { search: query } });
      setCustomerResults(response.data);
    } catch (error) {
      toast.error('Error al buscar clientes');
      setCustomerResults([]);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchCustomers(customerQuery);
    }, 500); // Pequeño delay para no bombardear la API al escribir

    return () => clearTimeout(delayDebounceFn);
  }, [customerQuery, searchCustomers]);

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery(''); // Limpia la búsqueda
    setCustomerResults([]); // Oculta los resultados
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerQuery('');
    setCustomerResults([]);
  };

  const filteredProducts = products.filter(p =>
    searchQuery && (p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.brand?.toLowerCase().includes(searchQuery.toLowerCase()))
  );


  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      if (existing.quantity >= product.stock) {
        toast.error('Stock insuficiente');
        return;
      }
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, total_item_pyg: (item.quantity + 1) * item.price_pyg }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price_pyg: product.price_pyg,
        total_item_pyg: product.price_pyg
      }]);
    }
    setSearchQuery('');
    searchInputRef.current.focus();
    toast.success(`${product.name} agregado al carrito`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty, total_item_pyg: newQty * item.price_pyg };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total_item_pyg, 0);
  const total = subtotal;

  const handleNewCustomer = async () => {
    if (!newCustomer.name || !newCustomer.last_name || !newCustomer.doc_number) {
      toast.error('Nombre, apellido y número de documento son obligatorios');
      return;
    }

    const customerData = {
      name: newCustomer.name,
      last_name: newCustomer.last_name,
      contact_number: newCustomer.contact_number,
      email: newCustomer.email
    };

    if (newCustomer.doc_number) {
      if (docType === 'ci') {
        customerData.ci = newCustomer.doc_number;
      } else if (docType === 'ruc') {
        customerData.ruc = newCustomer.doc_number;
      }
    }

    try {
      const response = await axios.post(`${API}/clients/`, customerData);
      const createdCustomer = response.data;
      selectCustomer(createdCustomer);
      setNewCustomer({
        name: '',
        last_name: '',
        doc_number: '',
        contact_number: '',
        email: ''
      });
      setDocType('ci');
      setIsNewCustomerOpen(false);
      toast.success('Cliente creado exitosamente');
    } catch (error) {
      toast.error('Error al crear el cliente');
    }

  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    try {
      await axios.post(`${API}/sales/`, {
        items: cart,
        subtotal_pyg: subtotal,
        total_pyg: total,
        currency: 'PYG',
        exchange_rate: exchangeRate,
        payment_method: paymentMethod,
        client_id: selectedCustomer?.id || null,
        client_name: selectedCustomer?.name || null,
        client_ci_ruc: selectedCustomer?.ci || selectedCustomer?.ruc || null,
        client_ci_ruc: selectedCustomer?.ci || selectedCustomer?.ruc || null,
        salesperson_id: user.id,
        salesperson_name: user.name,

        is_credit_sale: isCreditSale,
        total_installments: isCreditSale ? parseInt(selectedInstallments) : null,

      });

      toast.success('¡Venta completada exitosamente!');
      setCart([]);
      setIsCheckoutOpen(false);
      clearCustomer();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar la venta');
    }
  };


  // Calculate totals for display
  const getSaleCalculations = () => {
    let discountRate = 0;
    let discountAmount = 0;

    if (selectedCustomer) {
      const clientType = selectedCustomer.client_type || 'Retail';
      const rateObj = discountRates.find(r => r.client_type === clientType);
      if (rateObj) {
        discountRate = rateObj.rate;
        discountAmount = subtotal * discountRate;
      }
    }

    const totalAfterDiscount = subtotal - discountAmount;
    const finalTotal = totalAfterDiscount + installmentInterest;

    return {
      discountRate,
      discountAmount,
      totalAfterDiscount,
      finalTotal
    };
  };

  const { discountRate, discountAmount, finalTotal } = getSaleCalculations();

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold" data-font="display">Nueva Venta</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Product Search */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle data-font="display">Buscar Productos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Input
                  placeholder="Buscar por nombre, SKU o marca..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="sales-search-input"
                  className="w-full"
                  ref={searchInputRef}

                />
                {searchQuery && (
                  <div className="border rounded-md max-h-80 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No se encontraron productos
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredProducts.slice(0, 10).map((product) => (
                          <div
                            key={product.id}
                            onClick={() => addToCart(product)}
                            className="p-3 hover:bg-secondary cursor-pointer transition-colors"
                            data-testid="sales-add-to-cart-button"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{product.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {product.sku} • {product.brand || 'Sin marca'} • Stock: {product.stock}
                                </p>
                              </div>
                              <p className="font-medium">{formatPYG(product.price_pyg)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cart */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" data-font="display">
                <ShoppingCart className="size-5" />
                Carrito ({cart.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">El carrito está vacío</p>
                ) : (
                  cart.map((item) => (
                    <div key={item.product_id} className="flex items-start justify-between py-2 border-b" data-testid="cart-item">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">{formatPYG(item.price_pyg)} c/u</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="size-6"
                            onClick={() => updateQuantity(item.product_id, -1)}
                          >
                            <Minus className="size-3" />
                          </Button>
                          <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="size-6"
                            onClick={() => updateQuantity(item.product_id, 1)}
                          >
                            <Plus className="size-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="size-6 ml-auto"
                            onClick={() => removeFromCart(item.product_id)}
                          >
                            <Trash2 className="size-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <p className="font-medium ml-2">{formatPYG(item.total_item_pyg)}</p>
                    </div>
                  ))
                )}
              </div>

              {cart.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium">{formatPYG(subtotal)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg">
                      <span data-font="display">Total</span>
                      <span data-font="display">{formatPYG(total)}</span>
                    </div>
                  </div>
                  <Button
                    className="w-full mt-4 bg-[hsl(var(--primary))] hover:bg-[#BFA774]"
                    onClick={() => setIsCheckoutOpen(true)}
                    data-testid="sales-checkout-button"
                  >
                    Proceder al Pago
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={isCheckoutOpen} onOpenChange={setIsCheckoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-font="display">Completar Venta</DialogTitle>
            <DialogDescription>Por favor, complete los campos siguientes para completar la venta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customer-search" className="flex items-center justify-between">
                Cliente (Opcional)
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsNewCustomerOpen(true)}
                >
                  <UserPlus className="size-4 mr-2" /> Nuevo Cliente
                </Button>
              </Label>

              {selectedCustomer ? (
                // Cliente Seleccionado
                <div className="p-3 border rounded-md flex justify-between items-center bg-secondary/50">
                  <div>
                    <p className="font-semibold flex items-center gap-2"><UserCheck className="size-4 text-green-600" /> {selectedCustomer.name} {selectedCustomer.last_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedCustomer.ci || selectedCustomer.ruc || 'Sin datos adicionales'}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearCustomer}>
                    Cambiar
                  </Button>
                </div>
              ) : (
                // Búsqueda de Cliente
                <div className="relative">
                  <Input
                    id="customer-search"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Buscar por nombre, email o teléfono..."
                    icon={<Search className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />}
                    className="pl-10"
                  />
                  {customerQuery.length >= 3 && customerResults.length > 0 && (
                    <div className="absolute z-10 w-full bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                      {customerResults.map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => selectCustomer(customer)}
                          className="p-3 hover:bg-secondary cursor-pointer transition-colors"
                        >
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {customer.ci || customer.ruc || 'Sin datos adicionales'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {customerQuery.length >= 3 && customerResults.length === 0 && (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      No se encontraron clientes. Usa el botón "Nuevo Cliente" para registrarlo.
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Logic for Sale Type (Contado vs Cuotas) */}
            {selectedCustomer?.allow_credit ? (
              <div className="space-y-4 pt-2 border-t">
                <div className="space-y-3">
                  <Label>Tipo de Venta</Label>
                  <div className="flex gap-4">
                    <div
                      className={`flex-1 p-3 border rounded-md cursor-pointer transition-all ${!isCreditSale ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'hover:bg-secondary'}`}
                      onClick={() => {
                        setIsCreditSale(false);
                        setPaymentMethod('cash'); // Reset to default or keep previous?
                        setSelectedInstallments('');
                        setInstallmentInterest(0);
                      }}
                    >
                      <div className="font-semibold">Contado</div>
                      <div className="text-xs text-muted-foreground">Pago inmediato</div>
                    </div>
                    <div
                      className={`flex-1 p-3 border rounded-md cursor-pointer transition-all ${isCreditSale ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'hover:bg-secondary'}`}
                      onClick={() => {
                        setIsCreditSale(true);
                        setPaymentMethod('credit'); // Internal marker for backend if needed, generally ignored for credit sales
                      }}
                    >
                      <div className="font-semibold">Crédito / Cuotas</div>
                      <div className="text-xs text-muted-foreground">Pago en cuotas</div>
                    </div>
                  </div>
                </div>

                {isCreditSale ? (
                  // VENTA A CUOTAS
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                    <Label>Plan de Cuotas</Label>
                    <Select
                      value={selectedInstallments ? selectedInstallments.toString() : ''}
                      onValueChange={(val) => {
                        const count = parseInt(val);
                        setSelectedInstallments(count);
                        const rule = installmentRules.find(r => r.installments === count);
                        if (rule) {
                          const interest = total * (rule.interest_rate / 100);
                          setInstallmentInterest(interest);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione cantidad de cuotas" />
                      </SelectTrigger>
                      <SelectContent>
                        {installmentRules.sort((a, b) => a.installments - b.installments).map((rule) => (
                          <SelectItem key={rule.id} value={rule.installments.toString()}>
                            {rule.installments} Cuotas ({rule.interest_rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedInstallments && (
                      <div className="text-sm text-muted-foreground mt-1 bg-secondary/50 p-2 rounded">
                        <div className="flex justify-between">
                          <span>Interés:</span>
                          <span>+{formatPYG(installmentInterest)}</span>
                        </div>
                        <div className="flex justify-between font-medium mt-1">
                          <span>Total Final:</span>
                          <span>{formatPYG(total + installmentInterest)}</span>
                        </div>
                        <div className="text-xs mt-1 text-right">
                          {selectedInstallments} cuotas de {formatPYG((total + installmentInterest) / selectedInstallments)}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // VENTA AL CONTADO (Standard Payment Method)
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                    <Label htmlFor="payment">Método de Pago *</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Efectivo</SelectItem>
                        <SelectItem value="card">Tarjeta</SelectItem>
                        <SelectItem value="transfer">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ) : (
              // NON-ELIGIBLE CLIENT (Existing Simple Logic)
              <div className="space-y-2">
                <Label htmlFor="payment">Método de Pago *</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Efectivo</SelectItem>
                    <SelectItem value="card">Tarjeta</SelectItem>
                    <SelectItem value="transfer">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatPYG(subtotal)}</span>
              </div>

              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuento ({(discountRate * 100).toFixed(0)}%)</span>
                  <span>- {formatPYG(discountAmount)}</span>
                </div>
              )}

              {isCreditSale && installmentInterest > 0 && (
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Interés Cuotas</span>
                  <span>+ {formatPYG(installmentInterest)}</span>
                </div>
              )}

              <Separator />

              <div className="flex justify-between text-lg font-bold">
                <span data-font="display">Total a Pagar</span>
                <span data-font="display">{formatPYG(finalTotal)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Moneda: Gs.</p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setIsCheckoutOpen(false)} className="flex-1">Cancelar</Button>
              <Button
                onClick={handleCheckout}
                className="flex-1 bg-[hsl(var(--primary))] hover:bg-[#BFA774]"
                data-testid="sales-complete-sale-button"
              >
                Completar Venta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* --- Modal de Registro de Nuevo Cliente --- */}
      <Dialog open={isNewCustomerOpen} onOpenChange={setIsNewCustomerOpen}>
        <DialogContent >
          <DialogHeader>
            <DialogTitle data-font="display">Registrar Nuevo Cliente</DialogTitle>
            <DialogDescription>
              Por favor, complete los campos siguientes para registrar un nuevo cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-customer-name">Nombre *</Label>
              <Input
                id="new-customer-name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="Juan Pérez"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-customer-last_name">Apellido *</Label>
              <Input
                id="new-customer-last_name"
                value={newCustomer.last_name}
                onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })}
                placeholder="Pérez"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Documento de Identidad (CI o RUC)</Label>
              <div className="flex gap-2">
                {/* Selector de Tipo de Documento */}
                <div className="w-1/3">
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ci">CI</SelectItem>
                      <SelectItem value="ruc">RUC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Input del Número de Documento */}
                <div className="w-2/3">
                  <Input
                    id="new-customer-doc-number"
                    value={newCustomer.doc_number}
                    onChange={(e) => setNewCustomer({ ...newCustomer, doc_number: e.target.value })}
                    placeholder={docType === 'ci' ? 'Ej: 12345678' : 'Ej: 12345678-9'}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-customer-email">Correo Electrónico</Label>
              <Input
                id="new-customer-email"
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder="cliente@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-customer-phone">Teléfono</Label>
              <Input
                id="new-customer-phone"
                value={newCustomer.contact_number}
                onChange={(e) => setNewCustomer({ ...newCustomer, contact_number: e.target.value })}
                placeholder="+595 0981234567"
              />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="ghost" onClick={() => setIsNewCustomerOpen(false)}>Cancelar</Button>
            <Button onClick={handleNewCustomer} className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
              Guardar y Seleccionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
